import React, { useMemo, useState } from 'react';
import { Table, Input, Select, Dropdown, Popconfirm, Button, Grid } from 'antd';
import {
  filterMembers,
  sortMembers,
  formatRelativeTime,
  getDisplayName,
  getInitials,
} from './dashboardUtils';
import './dashboard.css';

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'User' },
];
const ROLE_FILTER_OPTIONS = [{ value: 'all', label: 'All roles' }, ...ROLE_OPTIONS];
const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];
const PAGE_SIZE = 10;

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const roleLabel = (role) => (role ? role.charAt(0).toUpperCase() + role.slice(1) : '—');

export default function TeamMembersTable({
  users,
  currentUser,
  isAdmin,
  onInvite,
  onChangeRole,
  onSetActive,
}) {
  const screens = Grid.useBreakpoint();
  const isPhone = screens.md === false;
  const [query, setQuery] = useState('');
  const [role, setRole] = useState('all');
  const [status, setStatus] = useState('all');
  const [sort, setSort] = useState({ key: null, direction: null });
  const [pending, setPending] = useState({});
  const [confirmId, setConfirmId] = useState(null);

  const all = useMemo(() => users || [], [users]);
  const visible = useMemo(() => {
    const filtered = filterMembers(all, { query, role, status });
    return sort.key && sort.direction ? sortMembers(filtered, sort.key, sort.direction) : filtered;
  }, [all, query, role, status, sort]);

  const isSelf = (u) => currentUser && currentUser.user_id === u.id;
  const clearFilters = () => {
    setQuery('');
    setRole('all');
    setStatus('all');
  };

  const run = async (id, fn) => {
    setPending((p) => ({ ...p, [id]: true }));
    try {
      await fn();
    } catch (e) {
      // errors are surfaced by the parent
    } finally {
      setPending((p) => ({ ...p, [id]: false }));
    }
  };

  const changeRole = (u, value) => run(u.id, () => onChangeRole(u.id, value));
  const setActive = (u, value) => run(u.id, () => onSetActive(u.id, value));

  const renderStatus = (u) => (
    <span className={`status ${u.is_active ? 'active' : 'inactive'}`}>
      {u.is_active ? 'Active' : 'Inactive'}
    </span>
  );

  const renderUser = (u) => (
    <div className="member-user">
      <span className="member-avatar" aria-hidden="true">{getInitials(u)}</span>
      <div className="member-user-text">
        <div className="member-name" data-testid="member-name">
          {getDisplayName(u)}
          {isSelf(u) && <span className="member-you"> (you)</span>}
        </div>
        {u.email && getDisplayName(u) !== u.email && (
          <div className="member-email">{u.email}</div>
        )}
      </div>
    </div>
  );

  const renderRole = (u) =>
    isSelf(u) ? (
      roleLabel(u.role)
    ) : (
      <Select
        size="small"
        value={u.role}
        options={ROLE_OPTIONS}
        loading={!!pending[u.id]}
        disabled={!!pending[u.id]}
        aria-label={`Role for ${getDisplayName(u)}`}
        onChange={(value) => changeRole(u, value)}
        style={{ minWidth: 100 }}
      />
    );

  const renderActions = (u) => {
    if (isSelf(u)) return null;
    const name = getDisplayName(u);
    const trigger = (
      <Button
        size="small"
        loading={!!pending[u.id]}
        aria-label={`Actions for ${name}`}
      >
        •••
      </Button>
    );
    if (u.is_active) {
      return (
        <Popconfirm
          title={`Deactivate ${name}?`}
          description="They will no longer be able to sign in."
          okText="Deactivate"
          okButtonProps={{ danger: true }}
          open={confirmId === u.id}
          onOpenChange={(open) => !open && setConfirmId(null)}
          onConfirm={() => {
            setConfirmId(null);
            return setActive(u, false);
          }}
          onCancel={() => setConfirmId(null)}
        >
          <Dropdown
            trigger={['click']}
            menu={{
              items: [{ key: 'deactivate', label: 'Deactivate', danger: true }],
              onClick: () => setConfirmId(u.id),
            }}
          >
            {trigger}
          </Dropdown>
        </Popconfirm>
      );
    }
    return (
      <Dropdown
        trigger={['click']}
        menu={{
          items: [{ key: 'reactivate', label: 'Reactivate' }],
          onClick: () => setActive(u, true),
        }}
      >
        {trigger}
      </Dropdown>
    );
  };

  const columns = [
    {
      title: 'User',
      key: 'name',
      sorter: true,
      sortOrder: sort.key === 'name' ? sort.direction : null,
      render: (_, u) => renderUser(u),
    },
    ...(isAdmin
      ? [
          {
            title: 'Role',
            key: 'role',
            sorter: true,
            sortOrder: sort.key === 'role' ? sort.direction : null,
            render: (_, u) => renderRole(u),
          },
        ]
      : []),
    {
      title: 'Status',
      key: 'status',
      sorter: true,
      sortOrder: sort.key === 'status' ? sort.direction : null,
      render: (_, u) => renderStatus(u),
    },
    ...(isAdmin
      ? [
          {
            title: 'Last active',
            key: 'last_login',
            sorter: true,
            sortOrder: sort.key === 'last_login' ? sort.direction : null,
            render: (_, u) => formatRelativeTime(u.last_login),
          },
          {
            title: 'Joined',
            key: 'created_at',
            sorter: true,
            sortOrder: sort.key === 'created_at' ? sort.direction : null,
            render: (_, u) => formatDate(u.created_at),
          },
          {
            title: '',
            key: 'actions',
            align: 'right',
            render: (_, u) => renderActions(u),
          },
        ]
      : []),
  ];

  const empty = (
    <div className="members-empty">
      <div>No members match your filters</div>
      <Button type="link" onClick={clearFilters}>Clear filters</Button>
    </div>
  );

  return (
    <div className="dashboard-card members-tile">
      <div className="members-header">
        <h2>
          Team members <span className="members-count">{all.length}</span>
        </h2>
        <div className={`members-toolbar${isPhone ? ' stacked' : ''}`}>
          <Input.Search
            allowClear
            placeholder="Search members"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="members-search"
          />
          {isAdmin && (
            <Select
              value={role}
              options={ROLE_FILTER_OPTIONS}
              onChange={setRole}
              aria-label="Filter by role"
              className="members-filter"
            />
          )}
          <Select
            value={status}
            options={STATUS_FILTER_OPTIONS}
            onChange={setStatus}
            aria-label="Filter by status"
            className="members-filter"
          />
          {isAdmin && (
            <Button type="primary" onClick={onInvite}>
              Invite user
            </Button>
          )}
        </div>
      </div>

      {isPhone ? (
        <div className="members-cards">
          {visible.length === 0
            ? empty
            : visible.map((u) => (
                <div className="member-card" data-testid="member-card" key={u.id}>
                  <div className="member-card-top">
                    {renderUser(u)}
                    {isAdmin && renderActions(u)}
                  </div>
                  <div className="member-card-meta">
                    {renderStatus(u)}
                    {isAdmin && <div>{renderRole(u)}</div>}
                    {isAdmin && <div className="member-muted">Last active: {formatRelativeTime(u.last_login)}</div>}
                    {isAdmin && <div className="member-muted">Joined: {formatDate(u.created_at)}</div>}
                  </div>
                </div>
              ))}
        </div>
      ) : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={visible}
          locale={{ emptyText: empty }}
          pagination={visible.length > PAGE_SIZE ? { pageSize: PAGE_SIZE, showSizeChanger: false } : false}
          showSorterTooltip={false}
          onChange={(_p, _f, sorter) => {
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            setSort({ key: s?.order ? s.columnKey : null, direction: s?.order || null });
          }}
        />
      )}
    </div>
  );
}
